import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';
import { bindActionCreators } from 'redux';

import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectCurrentSalonLists } from 'selectors/currentSalon.selectors';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isTruthy from 'utils/predicates/isTruthy.util';

import { getClientsList } from 'salon/actions';
import SchedulerLoading from 'scheduler/SchedulerLoading.component';
import SchedulerRouter from 'scheduler/SchedulerRouter.component';

const L = logger('SchedulerLoader');

const M_LD_CLIENTS = 'Loading Client Lists...';
const M_LD_STYLISTS = 'Loading Stylist and Services...';
const M_LD_SCHEDULE = 'Loading Schedule Data...';
const M_NO_DATA = 'No Stylists? Contact Support';

const SchedulerLoader = ({ lists, getClientsList, fetchStylistsBySalon }) => {
  const salonId = useCurrentSalonId();

  // NOTE: there is a difference between null and []
  const hasRequestedClients = isTruthy(lists?.clients);
  const hasRequestedStylists = isTruthy(lists?.stylists);

  const hasClients = isFullArray(lists?.clients);
  const hasStylists = isFullArray(lists?.stylists);

  const resultClients = useAsync(async () => {
    if (hasClients || !salonId) return;
    return getClientsList(salonId);
  }, [getClientsList, hasClients, salonId]);

  const resultStylists = useAsync(async () => {
    if (hasStylists || !salonId) return;
    return fetchStylistsBySalon(salonId);
  }, [fetchStylistsBySalon, hasStylists, salonId]);

  const isLoadingClients = resultClients.loading;
  const isLoadingStylists = resultStylists.loading;

  const dataAttributes = useMemo(
    () => ({
      salonId,
      hasClients,
      hasStylists,
      hasRequestedClients,
      hasRequestedStylists,
      isLoadingClients,
      isLoadingStylists,
    }),
    [salonId, hasClients, hasStylists, hasRequestedClients, hasRequestedStylists, isLoadingClients, isLoadingStylists],
  );

  const message = isLoadingClients
    ? M_LD_CLIENTS
    : isLoadingStylists
      ? M_LD_STYLISTS
      : hasStylists
        ? M_LD_SCHEDULE
        : M_NO_DATA;

  L.debug('()', message);

  return hasRequestedClients && hasRequestedStylists && !isLoadingClients && !isLoadingStylists && hasStylists ? (
    <SchedulerRouter dataAttributes={dataAttributes} />
  ) : (
    <SchedulerLoading dataAttributes={dataAttributes} message={message} />
  );
};

const SchedulerLoaderHoc = connect(
  (state) => ({
    lists: selectCurrentSalonLists(state),
  }),
  (dispatch) => bindActionCreators({ getClientsList, fetchStylistsBySalon }, dispatch),
)(SchedulerLoader);

export default SchedulerLoaderHoc;
